import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { LIVE_NUMBER_TEST_STATUSES, MESSAGE_STATES } from '../../shared/models/test-group.model';
import { AuthUser, Role } from '../../shared/models/user.model';
import { BackendChannelService } from '../../shared/services/backend-channel.service';
import { BrowserUtils } from '../../shared/services/browser-utils';
import { CustomUtils } from '../../shared/services/custom-utils';
import { ModalService } from '../../shared/services/modal.service';
import { StopWordsService } from '../../shared/services/stop-words.service';
import { UsersService } from '../../shared/services/users.service';

@Component({
  standalone: false,
  selector: 'app-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.scss'],
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 1,
          transform: 'translateY(0)',
        })
      ),
      state(
        'closed',
        style({
          opacity: 0,
          transform: 'translateY(100%)',
        })
      ),
      transition('void => *', [style({ opacity: 0, transform: 'translateY(100%)' }), animate('0.3s')]),
      transition('* => void', [
        animate(
          '0.2s',
          style({
            opacity: 0,
            transform: 'translateY(100%)',
          })
        ),
      ]),
    ]),
  ],
})
export class InfoComponent implements OnInit {
  currentInfo = '';
  currentLinkForDocumentation = '';
  currentNavigationPath = '';
  showFabContent = false;
  dlrStatuses: { id: string; label: string }[] = [];
  receiptStatuses: { id: string; label: string }[] = [];
  stopWords: string[] = [];
  currentUser: AuthUser;
  showFabButton = true;
  showFlashCallHelp = false;

  urls: InfoUrls = {
    liveTestingModalTpl: ['/live-testing'],
    manualTestingModalTpl: ['/manual-testing'],
    mobileOriginatedModalTpl: ['/mo-testing'],
    cliTestingModalTpl: ['/cli-testing'],
    sipCliTestingModalTpl: ['/sip/testing'],
    sessionsModalTpl: ['/sessions', '/sessions/create'],
  };

  pathsToShowDocumentation: string[] = [
    '/dashboard',
    '/live-testing',
    '/mo-testing',
    '/manual-testing',
    '/cli-testing',
    '/sip/testing',
    '/scheduler/tasks',
    '/scheduler/reports',
    '/suppliers',
    '/sessions',
    '/sender-templates',
    '/text-templates',
    '/test-case-templates',
    '/content-templates',
    '/user/account',
    '/user/list',
    '/user/account/api',
    '/pricing-general',
    '/pricing-special',
    '/billing/postpaid',
    '/billing/prepaid',
    '/invoices',
    '/audit/logs',
    '/other/reported-numbers',
  ];

  modalUrlMap: any = {
    '/dashboard': 'https://help.telqtele.com/tp/dashboard',
    '/live-testing': 'https://help.telqtele.com/tp/live-number-testing',
    '/mo-testing': 'https://help.telqtele.com/tp/mo-testing',
    '/manual-testing': 'https://help.telqtele.com/tp/manual-testing',
    '/cli-testing': 'https://help.telqtele.com/tp/cli-testing',
    '/sip/testing': 'https://help.telqtele.com/tp/sip-cli-testing',
    '/scheduler/tasks': 'https://help.telqtele.com/tp/Scheduler.1657962529.html',
    '/scheduler/reports': 'https://help.telqtele.com/tp/Report.1705312436.html',
    '/suppliers': 'https://help.telqtele.com/tp/Suppliers.1658159112.html',
    '/sessions': 'https://help.telqtele.com/tp/smpp-suppliers',
    '/sender-templates': 'https://help.telqtele.com/tp/Templates.1657372676.html',
    '/text-templates': 'https://help.telqtele.com/tp/Templates.1657372676.html',
    '/test-case-templates': 'https://help.telqtele.com/tp/Templates.1657372676.html',
    '/content-templates': 'https://help.telqtele.com/tp/Templates.1657372676.html',
    '/user/account': 'https://help.telqtele.com/tp/Account-settings.1657438253.html',
    '/user/list': 'https://help.telqtele.com/tp/Account-settings.1657438253.html',
    '/user/account/api': 'https://help.telqtele.com/tp/API---REST-API-setup.1658093569.html',
    '/pricing-general': 'https://help.telqtele.com/tp/Prices.2870738945.html',
    '/pricing-special': 'https://help.telqtele.com/tp/Prices.2870738945.html',
    '/billing/postpaid': 'https://help.telqtele.com/tp/Prices.2870738945.html',
    '/billing/prepaid': 'https://help.telqtele.com/tp/Prices.2870738945.html',
    '/invoices': 'https://help.telqtele.com/tp/Invoices.1657569287.html',
    '/audit/logs': 'https://help.telqtele.com/tp/Logs.1657405596.html',
    '/other/reported-numbers': 'https://help.telqtele.com/tp/reported-numbers',
  };

  watchTutorialUrlMap: Record<string, string> = {
    '/live-testing': 'https://www.youtube.com/watch?v=gRImmBN8jcE&feature=youtu.be',
    '/suppliers': 'http://youtube.com/watch?v=Fhz50U6UBJo&feature=youtu.be',
    '/sessions': 'http://youtube.com/watch?v=Fhz50U6UBJo&feature=youtu.be',
    '/sessions/create': 'http://youtube.com/watch?v=Fhz50U6UBJo&feature=youtu.be',
    '/user/account/api': 'http://youtube.com/watch?v=Fhz50U6UBJo&feature=youtu.be',
    '/scheduler/tasks': 'https://www.youtube.com/watch?v=LCY2Z5EJzoA&feature=youtu.be',
    '/scheduler/reports': 'https://www.youtube.com/watch?v=LCY2Z5EJzoA&feature=youtu.be',
  };

  @ViewChild('sessionsModalTpl', { read: TemplateRef, static: false }) sessionsModalTpl: any;
  @ViewChild('liveTestingModalTpl', { read: TemplateRef, static: false }) liveTestingModalTpl: any;
  @ViewChild('manualTestingModalTpl', { read: TemplateRef, static: false }) manualTestingModalTpl: any;
  @ViewChild('cliTestingModalTpl', { read: TemplateRef, static: false }) cliTestingModalTpl: any;
  @ViewChild('sipCliTestingModalTpl', { read: TemplateRef, static: false }) sipCliTestingModalTpl: any;
  @ViewChild('mobileOriginatedModalTpl', { read: TemplateRef, static: false }) mobileOriginatedModalTpl: any;
  @ViewChild('pricingModalTpl', { read: TemplateRef, static: false }) pricingModalTpl: any;
  @ViewChild('invoiceModalTpl', { read: TemplateRef, static: false }) invoiceModalTpl: any;
  @ViewChild('generalModalTpl', { read: TemplateRef, static: false }) generalModalTpl: any;
  @ViewChild('appInfoModalTpl', { read: TemplateRef, static: false }) appInfoModalTpl: any;

  modals = [
    'sessionsModalTpl',
    'liveTestingModalTpl',
    'manualTestingModalTpl',
    'cliTestingModalTpl',
    'sipCliTestingModalTpl',
    'mobileOriginatedModalTpl',
    'pricingModalTpl',
    'invoiceModalTpl',
    'generalModalTpl',
  ];

  hideFabButtonsOnRoutes: string[] = ['/users'];

  constructor(
    public stopWordsService: StopWordsService,
    public router: Router,
    public modal: ModalService,
    private elementRef: ElementRef,
    private userService: UsersService,
    private backendChannel: BackendChannelService
  ) {}

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showFabContent = false;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const yOffset = window.scrollY;
    const windowHeight = window.innerHeight;
    const bodyHeight = document.body.offsetHeight - 100;

    // Check if user has scrolled to bottom
    if (yOffset + windowHeight >= bodyHeight) {
      this.showFabButton = false;
    } else {
      this.showFabButton = true;
    }
  }

  ngOnInit() {
    const oldTrigger = document.getElementById('app-info-trigger');
    if (oldTrigger) {
      oldTrigger.remove();
    }
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = this.getPathOnly(event.urlAfterRedirects);
        this.currentInfo = this.getInfoByUrl(url);
        this.currentNavigationPath = url;
        this.currentLinkForDocumentation = this.modalUrlMap[this.currentNavigationPath] || '#';
        const shouldFabBeHidden = this.hideFabButtonsOnRoutes.some(r => this.currentNavigationPath.includes(r));
        setTimeout(() => {
          this.showFabButton = !shouldFabBeHidden;
        }, 100);
      }
    });
    MESSAGE_STATES.forEach(_ => this.dlrStatuses.push(_));
    LIVE_NUMBER_TEST_STATUSES.forEach(_ => this.receiptStatuses.push(_));
    this.currentInfo = this.getInfoByUrl(window.location.hash.replace('#', ''));
    this.currentUser = this.userService.authUser;
    this.userService.authUpdates.subscribe(user => {
      this.currentUser = user;
    });
  }

  onClickInfo() {
    if (this.modals.includes(this.currentInfo)) {
      this.updateStopWords();
      this.updateFlashCallHelp();
      this.modal.alert().component(this[this.currentInfo]).open();
      this.showFabContent = false;
    }
  }

  private updateStopWords() {
    const infoToServiceMap = {
      liveTestingModalTpl: 'lnt',
      manualTestingModalTpl: 'mnt',
      mobileOriginatedModalTpl: 'mo',
    };
    if (infoToServiceMap[this.currentInfo]) {
      this.stopWords = [];
      this.stopWordsService
        .contents(infoToServiceMap[this.currentInfo])
        .pipe(
          catchError(e => {
            this.stopWords = [];
            return throwError(() => e);
          })
        )
        .subscribe(data => {
          this.stopWords = data;
        });
    }
  }

  private updateFlashCallHelp() {
    if (this.currentInfo !== 'liveTestingModalTpl') {
      return;
    }
    const isAdmin = this.currentUser?.role === Role.ADMIN;
    this.userService.getFlashCallAccess().subscribe(access => (this.showFlashCallHelp = isAdmin ? access.featureEnabled : access.enabled));
  }

  getInfoByUrl(url: string): string {
    for (const info in this.urls) {
      if (this.urls[info].indexOf(url) !== -1) {
        return info;
      }
    }
    return '';
  }

  showDocumentaion(): boolean {
    if (this.currentNavigationPath === '') {
      this.currentNavigationPath = '/dashboard';
      this.currentLinkForDocumentation = this.modalUrlMap[this.currentNavigationPath];
      return true;
    }
    return this.pathsToShowDocumentation.includes(this.currentNavigationPath);
  }

  showPricing(): boolean {
    return !CustomUtils.getGuestUrls().includes(this.currentNavigationPath);
  }

  showHelpButton(): boolean {
    return !!this.currentInfo;
  }

  getIconForStatus(statusID: string): string {
    return CustomUtils.getIconForStatus(statusID);
  }

  openFreshChat() {
    fcWidget.open();
    this.showFabContent = false;
  }

  onClickWatchDemo() {
    this.showFabContent = false;
    const watchTutorialUrl = this.watchTutorialUrlMap[this.currentNavigationPath] || 'https://www.youtube.com/@telqsmstestingtool';
    window.open(watchTutorialUrl, '_blank');
  }

  onClickDocumentation() {
    this.showFabContent = false;
    window.open(this.currentLinkForDocumentation, '_blank');
  }

  clickCount = 0;
  clickTimer = null;
  appInfo = '';
  appInfoClick() {
    // triple click handler
    this.clickCount++;
    if (this.clickCount === 1) {
      this.clickTimer = setTimeout(() => {
        this.clickCount = 0;
      }, 500);
    }
    if (this.clickCount === 3) {
      clearTimeout(this.clickTimer);
      this.clickCount = 0;
      const intVersion = parseInt(BrowserUtils.getCurrentVersion());
      this.appInfo = JSON.stringify(
        {
          version: BrowserUtils.getCurrentVersion(),
          build: intVersion ? new Date(intVersion).toISOString() : 'unknown',
          device: {
            userAgent: window.navigator.userAgent,
            locale: window.navigator.language,
            time: new Date().toString(),
          },
          csrf: BrowserUtils.getAllXsrf(),
          backendChannel: this.backendChannel.$current.value,
          backendUrl: this.backendChannel.getBaseUrl(this.backendChannel.$current.value),
          user: this.userService.authUser
            ? {
                id: this.userService.authUser.id,
                impersonated: this.userService.authUser.impersonated,
                role: this.userService.authUser.role,
                enabled: this.userService.authUser.enabled,
                emailVerified: this.userService.authUser.emailVerified,
                showAllResults: this.userService.authUser.showAllResults,
                moAllowedNumbersEnabled: this.userService.authUser.moAllowedNumbersEnabled,
              }
            : null,
        },
        null,
        2
      );
      this.modal.alert().component(this.appInfoModalTpl).open();
    }
  }

  appInfoCopy() {
    BrowserUtils.copyToClipboard(this.appInfo);
  }

  private getPathOnly(url: string): string {
    const urlTree = this.router.parseUrl(url);
    urlTree.queryParams = {};
    urlTree.fragment = null;
    const pathName = urlTree.toString();

    return pathName;
  }
}

type InfoUrls = Record<string, string[]>;
